/**
 * Input mapping (spec §4). Keyboard defaults, structured so remapping is a data
 * change rather than a code change — accessibility requires fully remappable keys.
 *
 * Exposes edge-triggered (`pressed`/`released`) and level-triggered (`down`)
 * queries. `consume()` must be called once per fixed step, after the step has
 * read what it needs, so edges survive exactly one simulation tick regardless of
 * render rate.
 */

export type Action =
  | 'moveForward'
  | 'moveBack'
  | 'moveLeft'
  | 'moveRight'
  | 'sneak'
  | 'roll'
  | 'interact'
  | 'observe'
  | 'callHolly'
  /**
   * Her notes, on their own key (D-237). `observe` still raises them when
   * nothing is in reach, but a map that shares the watch key is unreachable
   * whenever something watchable is near — all of beat 10, with the herd at
   * her heels — and drops out from under the player the moment something
   * walks into reach while it is held.
   */
  | 'notes'
  | 'pause'
  /**
   * Silence everything. Not a verb of hers, and not in spec §4's control list —
   * but `pause` is not either, and a game that starts making noise without an
   * obvious way to stop it is hostile. Routed through `Input` rather than a
   * bare listener so it is remappable and blur-safe like everything else.
   */
  | 'muteAudio';

/** Spec §4 defaults. `event.code`, so layout-independent. */
export const DEFAULT_BINDINGS: Readonly<Record<Action, readonly string[]>> = {
  moveForward: ['KeyW', 'ArrowUp'],
  moveBack: ['KeyS', 'ArrowDown'],
  moveLeft: ['KeyA', 'ArrowLeft'],
  moveRight: ['KeyD', 'ArrowRight'],
  sneak: ['ControlLeft', 'ControlRight'],
  roll: ['ShiftLeft', 'ShiftRight'],
  interact: ['KeyE'],
  observe: ['KeyQ'],
  callHolly: ['KeyF'],
  // Not `M`: that is mute. Tab is the map key in enough games to be guessed,
  // and it is already swallowed like every bound key (see `attach`).
  notes: ['Tab'],
  pause: ['Escape'],
  muteAudio: ['KeyM'],
};

export class Input {
  #bindings: Record<Action, readonly string[]>;
  #down = new Set<Action>();
  #pressed = new Set<Action>();
  #released = new Set<Action>();
  #keysDown = new Set<string>();
  #touchHeld = new Set<Action>();
  #touchAxis = { x: 0, y: 0 };
  #touchAxisActive = false;
  #detach: (() => void) | null = null;
  #lookX = 0;
  #lookY = 0;
  #pointerLocked = false;

  constructor(bindings: Record<Action, readonly string[]> = { ...DEFAULT_BINDINGS }) {
    this.#bindings = bindings;
  }

  attach(target: Window | HTMLElement = window): void {
    const onKeyDown = (e: Event): void => {
      const ke = e as KeyboardEvent;
      const code = ke.code;
      const actions = this.#actionsFor(code);

      // Swallow the browser's own shortcut for any key WE have bound.
      //
      // This is not a nicety. Spec §4 binds sneak to Ctrl and forward to W, so
      // creeping forward — the most ordinary thing in a stealth game — presses
      // **Ctrl+W, which closes the tab.** It was reported as the game crashing
      // in both Firefox and Chromium, and it was neither a crash nor a browser
      // bug: the page was being closed exactly as asked. Ctrl+S, Ctrl+D and
      // Ctrl+F are all reachable the same way while sneaking.
      //
      // Escape is deliberately NOT swallowed — it is how you release the
      // pointer lock, and trapping a player inside the canvas is worse than any
      // shortcut it might trigger.
      // ...but never while the player is typing. Swallowing keys destined for a
      // text field would be a real accessibility fault, and this handler is on
      // `window`, so it sees every key on the page.
      const active = document.activeElement;
      const editing = active instanceof HTMLElement
        && (active.isContentEditable
          || active.tagName === 'INPUT'
          || active.tagName === 'TEXTAREA'
          || active.tagName === 'SELECT');
      if (actions.length > 0 && code !== 'Escape' && !editing) e.preventDefault();

      if (ke.repeat) return;
      this.#keysDown.add(code);
      for (const action of actions) this.#refreshAction(action);
    };
    const onKeyUp = (e: Event): void => {
      const code = (e as KeyboardEvent).code;
      this.#keysDown.delete(code);
      for (const action of this.#actionsFor(code)) this.#refreshAction(action);
    };
    // Losing focus mid-hold would otherwise leave an action stuck down forever.
    const onBlur = (): void => {
      for (const action of this.#down) this.#released.add(action);
      this.#down.clear();
      this.#keysDown.clear();
      this.#touchHeld.clear();
      this.#touchAxis = { x: 0, y: 0 };
      this.#touchAxisActive = false;
    };

    // Mouse look. Accumulated raw, in pixels — this class deliberately knows
    // nothing about sensitivity or pitch limits, which are camera decisions and
    // live in the camera's tuning (spec §4).
    //
    // Deltas are only taken while the pointer is LOCKED. Reading movement from
    // an unlocked pointer means the camera swings whenever the mouse crosses the
    // window, which reads as a bug even when it is doing exactly what it was told.
    const onMouseMove = (e: Event): void => {
      if (!this.#pointerLocked) return;
      const me = e as MouseEvent;
      this.#lookX += me.movementX;
      this.#lookY += me.movementY;
    };
    const onPointerLockChange = (): void => {
      this.#pointerLocked = document.pointerLockElement !== null;
      // A stale delta accumulated across a lock change would arrive as one jump.
      this.#lookX = 0;
      this.#lookY = 0;
    };

    target.addEventListener('keydown', onKeyDown);
    target.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('mousemove', onMouseMove);
    document.addEventListener('pointerlockchange', onPointerLockChange);

    this.#detach = (): void => {
      target.removeEventListener('keydown', onKeyDown);
      target.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
    };
  }

  /** True while the pointer is captured and mouse look is live. */
  get pointerLocked(): boolean {
    return this.#pointerLocked;
  }

  /**
   * Accumulated mouse movement in pixels since the last call, and it CLEARS on
   * read. The camera consumes this once per render frame; anything else reading
   * it would silently steal the camera's input.
   */
  takeLook(): { dx: number; dy: number } {
    const delta = { dx: this.#lookX, dy: this.#lookY };
    this.#lookX = 0;
    this.#lookY = 0;
    return delta;
  }

  /**
   * Test seam: feed synthetic mouse movement without a real pointer lock, so the
   * Playwright harness can drive the camera the way it drives the keyboard.
   */
  injectLook(dx: number, dy: number): void {
    this.#lookX += dx;
    this.#lookY += dy;
  }

  /**
   * Test/debug only: set a physical key's held state without dispatching a DOM
   * event. Manual fixed stepping has no RAF-owned input event opportunity, so
   * a browser event there would race the exact ticks it is meant to drive.
   */
  setKeyDown(code: string, held: boolean): void {
    if (held) this.#keysDown.add(code);
    else this.#keysDown.delete(code);
    for (const action of this.#actionsFor(code)) this.#refreshAction(action);
  }

  detach(): void {
    this.#detach?.();
    this.#detach = null;
    this.#down.clear();
    this.#pressed.clear();
    this.#released.clear();
    this.#keysDown.clear();
    this.#touchHeld.clear();
    this.#touchAxis = { x: 0, y: 0 };
    this.#touchAxisActive = false;
    this.#lookX = 0;
    this.#lookY = 0;
  }

  rebind(action: Action, codes: readonly string[]): void {
    this.#bindings = { ...this.#bindings, [action]: codes };
  }

  get bindings(): Readonly<Record<Action, readonly string[]>> {
    return this.#bindings;
  }

  down(action: Action): boolean {
    return this.#down.has(action);
  }
  pressed(action: Action): boolean {
    return this.#pressed.has(action);
  }
  released(action: Action): boolean {
    return this.#released.has(action);
  }

  /**
   * Accept analogue touch intent without making the controller care whether it
   * came from a glass surface or a key. `active` stays separate from magnitude
   * because a thumb resting inside the stick's dead zone must still suppress
   * keyboard movement rather than unexpectedly blending the two devices.
   */
  setTouchAxis(x: number, y: number, active: boolean): void {
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      this.#touchAxis = { x: 0, y: 0 };
      this.#touchAxisActive = false;
      return;
    }
    const length = Math.hypot(x, y);
    const scale = length > 1 ? 1 / length : 1;
    this.#touchAxis = { x: x * scale, y: y * scale };
    this.#touchAxisActive = active;
  }

  /**
   * Fold a touch button into the same edge bookkeeping as a key, so a hold
   * cannot become a separate, subtly different kind of action for the game.
   */
  setTouchAction(action: Action, held: boolean): void {
    if (held) this.#touchHeld.add(action);
    else this.#touchHeld.delete(action);
    this.#refreshAction(action);
  }

  /**
   * Camera-relative movement intent, magnitude clamped to 1 so diagonals are not
   * faster than cardinals (spec §0 Feel: no ice-skating, no cheap speed).
   */
  moveAxis(): { x: number; y: number } {
    if (this.#touchAxisActive) return { ...this.#touchAxis };
    const x = (this.down('moveRight') ? 1 : 0) - (this.down('moveLeft') ? 1 : 0);
    const y = (this.down('moveForward') ? 1 : 0) - (this.down('moveBack') ? 1 : 0);
    if (x === 0 && y === 0) return { x: 0, y: 0 };
    const len = Math.hypot(x, y);
    return { x: x / len, y: y / len };
  }

  /** Clear edge state. Call once per fixed step, after reading. */
  consume(): void {
    this.#pressed.clear();
    this.#released.clear();
  }

  #actionsFor(code: string): Action[] {
    const out: Action[] = [];
    for (const action of Object.keys(this.#bindings) as Action[]) {
      if (this.#bindings[action].includes(code)) out.push(action);
    }
    return out;
  }

  #refreshAction(action: Action): void {
    const keyboardHeld = this.#bindings[action].some((code) => this.#keysDown.has(code));
    const held = keyboardHeld || this.#touchHeld.has(action);
    if (held) {
      if (!this.#down.has(action)) {
        this.#down.add(action);
        this.#pressed.add(action);
      }
      return;
    }
    if (this.#down.delete(action)) this.#released.add(action);
  }
}
